import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getPendingUsers: async () => {
    const response = await api.get('/admin/users');
    const users = response.data;
    return users.filter(u => !u.is_approved);
  },
  approveUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/approve`);
    return response.data;
  },
  rejectUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/reject`);
    return response.data;
  },
  getAllUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  createUser: async (name, email, password) => {
    const response = await api.post('/admin/users/create', { name, email, password });
    return response.data;
  },
};

// Clients API
export const clientsAPI = {
  getAll: async (search = '') => {
    const response = await api.get('/clients', { params: { search } });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },
  create: async (clientData) => {
    const response = await api.post('/clients', clientData);
    return response.data;
  },
  seed: async () => {
    const response = await api.post('/seed/clients');
    return response.data;
  },
};

// Forms API
export const formsAPI = {
  getTemplates: async () => {
    const response = await api.get('/forms/templates');
    return response.data;
  },
  submit: async (formData) => {
    const response = await api.post('/forms/submit', formData);
    return response.data;
  },
  getCompleted: async (clientId = null) => {
    const params = clientId ? { client_id: clientId } : {};
    const response = await api.get('/forms/completed', { params });
    return response.data;
  },
  getCompletedById: async (formId) => {
    const response = await api.get(`/forms/completed/${formId}`);
    return response.data;
  },
  getPdfUrl: (formId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/forms/completed/${formId}/pdf?token=${token}`;
  },
};

// Signatures API
export const signaturesAPI = {
  save: async (clientId, signatureField, signatureData) => {
    const response = await api.post('/signatures/save', {
      client_id: clientId,
      signature_field: signatureField,
      signature_data: signatureData,
    });
    return response.data;
  },
  getAll: async (clientId) => {
    const response = await api.get(`/signatures/${clientId}`);
    return response.data;
  },
  delete: async (clientId, signatureField) => {
    const response = await api.delete(`/signatures/${clientId}/${signatureField}`);
    return response.data;
  },
};

export default api;
